import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openContactInInbox } from "../../utils/openContactInInbox";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { Add as AddIcon, ExportSquare } from "iconsax-react";
import { toast } from "react-hot-toast";
import { CATEGORY_CONFIG, getCategoryColumns, GROUPED_COLUMNS } from "./categoryReportsConfig";
import { fetchCategoryReportEvents, fetchCategoryReportEventsRequest } from "../../redux/Slice/Reports/CategoryReportsSlice";
import { reportsFilterPillSx, reportsDropdownPaperSx, reportsInfoTooltipSx } from "./reportsFilterStyles";
import { REPORTS_TOOLTIP_COPY } from "./reportsTooltipCopy";
import ReportsDateRangeControl from "./ReportsDateRangeControl";
import CategoryMemberFilterChip from "./CategoryMemberFilterChip";
import CategoryDurationFilterChip from "./CategoryDurationFilterChip";
import { downloadCsv } from "./reportsExportCsv";

const formatTimestamp = (isoString) => {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return isoString;
  const datePart = date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });
  const timePart = date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${datePart} at ${timePart}`;
};

// The "Time" column (Response/Resolution/First Response Time categories)
// was showing raw seconds straight from the API (e.g. "455.598") -- fixed
// 2026-08-15 to match the same "Xm Ys" style Overview's own duration
// columns already use, instead of a bare number nobody can read at a glance.
//
// Never cascaded to hours/days/weeks the way Overview's own formatters do
// (ReportsTable.jsx's formatHoursAndMinutes/formatMinutesAndSeconds,
// fixed there 2026-08-15) -- a real value like 10029s printed as the
// unreadable "167m 9s" instead of "2h 47m". Found 2026-08-16 when a
// backlog-inflated response time surfaced exactly that case live. Same
// cascade logic as those two, just combined into one seconds-in function
// since this file only ever has one shape of input (raw seconds from the
// event's own duration field, not a pre-divided minutes value like
// Overview's average columns use).
const formatSecondsDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "—";
  const totalMinutes = Math.floor(seconds / 60);
  const weeks = Math.floor(totalMinutes / (60 * 24 * 7));
  const days = Math.floor((totalMinutes % (60 * 24 * 7)) / (60 * 24));
  const hrs = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;

  if (weeks > 0) {
    let result = `${weeks}w`;
    if (days > 0) result += ` ${days}d`;
    return result;
  }
  if (days > 0) {
    let result = `${days}d`;
    if (hrs > 0) result += ` ${hrs}h`;
    return result;
  }
  if (hrs > 0) {
    let result = `${hrs}h`;
    if (mins > 0) result += ` ${mins}m`;
    return result;
  }

  const totalSeconds = Math.round(seconds);
  const secs = totalSeconds % 60;
  let result = "";
  if (mins > 0) result += `${mins}m`;
  if (secs > 0) {
    if (result) result += " ";
    result += `${secs}s`;
  }
  return result || "0s";
};

// Converts a duration filter's {operator, value1, value2, unit} into
// {durationMin, durationMax} SECONDS -- the wire unit the backend/DB store
// everywhere (matches Overview's own minutesToSeconds/operatorToRange
// pattern in ReportsTable.jsx, just unit-aware since this popover offers
// Seconds/Minutes/Hours, not a fixed minutes field).
// Mirrors categoryReports.service.js's GROUPED_MAX_RANGE_DAYS -- the real
// enforcement is server-side, this just keeps the picker from ever letting
// someone select something the backend would reject anyway.
const GROUPED_MAX_RANGE_DAYS = 31;

// Export-all-pages loop (see exportCsv below) -- pulls every row matching
// the current filters, not just the page on screen. PAGE_SIZE_MAX server-
// side is 100 (categoryReports.service.js), so that's the biggest ask per
// round trip. EXPORT_MAX_PAGES is a client-side safety cap only (flat/
// non-grouped categories have no server-side date-range limit the way
// grouped mode does) -- 200 pages * 100 rows = 20,000 rows matches the same
// order of magnitude as the backend's own MAX_EVENTS_FOR_GROUPING ceiling,
// so it's refusing at roughly the same "this needs a narrower filter"
// point the app already draws elsewhere, not an arbitrary new number.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 200;

const UNIT_TO_SECONDS = { Seconds: 1, Minutes: 60, Hours: 3600 };
const toSeconds = (value, unit) => {
  if (value === "" || value === null || value === undefined) return undefined;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return undefined;
  return Math.round(num * (UNIT_TO_SECONDS[unit] || 1));
};
const durationFilterToRange = (filterValue) => {
  if (!filterValue) return { durationMin: undefined, durationMax: undefined };
  const { operator, value1, value2, unit } = filterValue;
  if (operator === "gt") return { durationMin: toSeconds(value1, unit), durationMax: undefined };
  if (operator === "lt") return { durationMin: undefined, durationMax: toSeconds(value1, unit) };
  if (operator === "between") return { durationMin: toSeconds(value1, unit), durationMax: toSeconds(value2, unit) };
  return { durationMin: undefined, durationMax: undefined };
};

// Category drill-down view -- renders inside the SAME table Paper as the
// Overview aggregate table (ReportsTable.jsx swaps content, not the
// container). Columns are config-driven (categoryReportsConfig.js); rows
// come from the real /reports/category endpoint (Phase 4/5, 2026-08-15) via
// CategoryReportsSlice -- this component never hardcodes a category's field
// names, and the backend's row shape is built to match getCategoryColumns'
// keys exactly (conversationId/timestamp/performedBy + each category's
// extraColumns), so no remapping happens here.
//
// Filters: "+ Add filter" lists whatever CATEGORY_CONFIG[category].filters
// declares, minus whichever are already added as chips (mirrors bot.space's
// own "menu only shows what's left" behavior) -- but only once a category
// has 2+ filter fields. With exactly 1 (Assigned/Closed/Reassigned today),
// the "Add filter" step is pointless friction for a menu with one item, so
// that single field just renders as an always-on chip instead (Rahim's
// explicit call, 2026-08-12) -- no remove either, since there'd be no way
// to re-add it without "+ Add filter" existing. This is length-driven, not
// category-driven: once a category gains a 2nd filter, "+ Add filter"
// comes back on its own, no code change needed here.
// Two filter types exist: `type: "member"` (Assigned/Closed/Reassigned,
// team members only) and `type: "duration"` (Response/Resolution/First
// Response Time -- a time-range filter + a member filter).
//
// "Filter by Count" (Response/Resolution Time's includesCount toggle) --
// STILL NOT wired to real data (2026-08-15, Phase 5). Grouping-by-
// conversation across a cursor-paginated flat event list is a genuinely
// unresolved design problem (a conversation's events can span multiple
// pages, so a client-side group-then-count over just the current page would
// silently undercount) -- flagged, not solved, in
// reports-read-endpoint-frontend-plan memory. Toggling it on shows an
// honest "not available yet" state instead of either fake grouped numbers
// or a wrong/undercounted real one.
const CategoryReportDetail = forwardRef(({ category, startDate, endDate, onRangeChange, members, onExportingChange }, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const { rows: realRows, nextCursor: realNextCursor, loading, loadingMore, loadMoreError, error } = useSelector((state) => state.categoryReports);
  const { userDetails } = useSelector((state) => state.userDetails);

  // Conversation column -- 2026-08-15, Rahim's explicit call: show the raw
  // contact id (not a resolved name) as a clickable link straight into that
  // contact's real chat in Inbox. Same URL shape already used elsewhere for
  // this exact "jump to a contact's chat" action (see ContactsList.jsx's
  // handleSendMessage) -- not a new pattern.
  const goToConversation = (contactId) => {
    if (!contactId || !accountid || !userDetails?._id) return;
    // Looks up the contact's tab (Closed/Blocked) and hands it to the Inbox as a navigation hint.
    openContactInInbox({ dispatch, navigate, accountid, userid: userDetails._id, contactId });
  };

  const [addFilterAnchorEl, setAddFilterAnchorEl] = useState(null);
  const [activeFilterKeys, setActiveFilterKeys] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [countSortDir, setCountSortDir] = useState("desc");
  // Infinite scroll: the first page is fetched by the filter effect below; every later page
  // is fetched by loadMore() with the server's nextCursor and appended in the slice. Fixed
  // page size (the table scrolls now, so it no longer has to match how many rows fit on
  // screen); the backend caps it at 100.
  const PAGE_SIZE = 50;
  // Overview's max height in px, computed for THIS viewport. Null until the first
  // measurement lands (uses the calc() fallback below for that one frame).
  const [boxHeightPx, setBoxHeightPx] = useState(null);
  const outerBoxRef = useRef(null);
  const tableContainerRef = useRef(null);
  const rows = realRows;
  const nextCursor = realNextCursor;

  const label = CATEGORY_CONFIG[category]?.label || category;
  const availableFilters = CATEGORY_CONFIG[category]?.filters || [];
  const singleFilter = availableFilters.length === 1 ? availableFilters[0] : null;
  const remainingFilters = availableFilters.filter((f) => !activeFilterKeys.includes(f.key));

  const durationFilterValue = filterValues.duration;
  const isGrouped = Boolean(durationFilterValue?.countEnabled);

  // Resolves whichever member-type filter is currently active for this
  // category (the always-on single chip, OR one added via "+ Add filter")
  // -- categories with a duration filter (Response/Resolution/First
  // Response Time) carry a SEPARATE member filter (repliedBy/resolvedBy)
  // alongside it.
  const activeMemberFilterKey = singleFilter
    ? singleFilter.key
    : availableFilters.find((f) => f.type === "member" && activeFilterKeys.includes(f.key))?.key;
  const activeMemberFilterValue = activeMemberFilterKey ? filterValues[activeMemberFilterKey] : null;

  const { durationMin, durationMax } = durationFilterToRange(durationFilterValue);

  // Turning "Filter by Count" on while a wider range is already selected
  // (from before the toggle was on) would otherwise send a request the
  // backend's own 31-day cap immediately rejects -- clamp to the most
  // recent 31 days instead of surfacing that as an error the user didn't
  // cause. Only runs on the on-transition (isGrouped goes false -> true),
  // never fights a range the user is actively picking while it's already on.
  const wasGroupedRef = useRef(isGrouped);
  useEffect(() => {
    const justTurnedOn = isGrouped && !wasGroupedRef.current;
    wasGroupedRef.current = isGrouped;
    if (!justTurnedOn || !startDate || !endDate) return;
    const rangeDays = (endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000);
    if (rangeDays > GROUPED_MAX_RANGE_DAYS) {
      const clampedStart = new Date(endDate.getTime() - GROUPED_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000);
      onRangeChange(clampedStart, endDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrouped]);

  // Switching categories starts filters AND pagination fresh -- a filter
  // chip from "Assigned" has no meaning once you're looking at "Closed",
  // and a cursor from one category's query is meaningless for another's.
  useEffect(() => {
    setActiveFilterKeys([]);
    setFilterValues({});
    setCountSortDir("desc");
  }, [category]);

  // Fetch the FIRST page whenever anything that changes the RESULT SET changes -- date range,
  // the member filter, the duration filter, sort direction, or the category. Later pages are
  // loadMore()'s job (scroll), never this effect's: a cursor from an old filter set would be
  // meaningless against new filters, so a fresh first page always starts with no cursor.
  const buildFetchArgs = (cursor) => ({
    category,
    startTime: startDate,
    endTime: endDate,
    memberIds: activeMemberFilterValue ? [activeMemberFilterValue._id] : undefined,
    durationMin,
    durationMax,
    groupByCount: isGrouped || undefined,
    countSortDir: isGrouped ? countSortDir : undefined,
    cursor,
    pageSize: PAGE_SIZE,
  });
  const filterSignature = JSON.stringify({ category, startDate, endDate, memberId: activeMemberFilterValue?._id, durationMin, durationMax, isGrouped, countSortDir });
  useEffect(() => {
    // Same class of bug as Overview's null-date 400 (2026-08-17 audit,
    // startTime/endTime are backend-required): startDate/endDate are
    // ReportsTable.jsx's own state, passed down as props, and start null
    // until a separate effect there sets the default range. This effect
    // has no reason to assume that's already happened by the time it fires.
    if (!startDate || !endDate) return;

    if (tableContainerRef.current) tableContainerRef.current.scrollTop = 0;
    dispatch(fetchCategoryReportEvents(buildFetchArgs(undefined)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterSignature]);

  const loadMore = () => {
    if (!nextCursor || loading || loadingMore || !startDate || !endDate) return;
    dispatch(fetchCategoryReportEvents(buildFetchArgs(nextCursor)));
  };

  // Fetch the next page when the user scrolls near the bottom of the table.
  const SCROLL_LOAD_THRESHOLD_PX = 200;
  const handleTableScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_LOAD_THRESHOLD_PX && !loadMoreError) loadMore();
  };

  // If a page doesn't fill the box (tall screen, short rows), there's nothing to scroll and
  // so nothing would ever trigger handleTableScroll -- keep loading until it overflows or the
  // rows run out. Stops on a load-more error (Retry row) instead of hammering the endpoint.
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || loading || loadingMore || loadMoreError || !nextCursor) return;
    if (el.scrollHeight <= el.clientHeight) loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, nextCursor, loading, loadingMore, loadMoreError, boxHeightPx]);

  // Sets the box's own height to Overview's real measured ceiling, so both tables are the
  // same size. The rows scroll inside it (infinite scroll), so nothing here depends on row
  // height or content any more.
  //
  // Deliberately NOT a ResizeObserver watching this table's own container -- setting a size
  // based on a measurement OF the thing you just resized creates a feedback loop. Reads this
  // box's own top offset instead (same technique as Overview's TableContainer in
  // ReportsTable.jsx), so both tables always agree on how much room is above them.
  useEffect(() => {
    const computeSizing = () => {
      const top = outerBoxRef.current?.getBoundingClientRect().top;
      if (top === undefined) return;
      // Matches Overview's own bottom buffer exactly (ReportsTable.jsx).
      const bottomBuffer = 32;
      const ceilingPx = window.innerHeight - top - bottomBuffer;
      // Same 240px floor as Overview's own guard -- never collapse to near-0 on a
      // transiently-wrong measurement.
      const nextBoxHeight = Math.max(240, Math.round(ceilingPx));
      setBoxHeightPx((prev) => (prev === nextBoxHeight ? prev : nextBoxHeight));
    };

    // Deferred one frame -- measuring synchronously on mount can catch this mid-route-transition.
    const rafId = requestAnimationFrame(computeSizing);
    window.addEventListener("resize", computeSizing);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", computeSizing);
    };
  }, [loading]);

  const columns = isGrouped ? GROUPED_COLUMNS : getCategoryColumns(category);

  // Exposed to ReportsTable.jsx's shared outer-bar Export button via ref
  // (see categoryDetailRef there). Fixed 2026-09-11 (was exporting only the
  // CURRENT page's rows, the ones actually on screen -- real bug, not just
  // a scoping choice): now re-fetches this same category/filters directly
  // from the endpoint in a loop, following `nextCursor` until it runs out,
  // instead of exporting the already-loaded `rows` (which is just one
  // page). Deliberately bypasses Redux (fetchCategoryReportEventsRequest,
  // not the fetchCategoryReportEvents thunk/dispatch) so this doesn't
  // overwrite whatever page is currently on screen or fight the thunk's
  // debounce -- it's its own isolated burst of sequential requests.
  const rowsToCsvRows = (exportColumns, exportRows) =>
    exportRows.map((row) =>
      exportColumns.map((col) => {
        if (col.key === "timestamp" || col.key === "lastTimestamp") return formatTimestamp(row[col.key]);
        if (col.key === "time") return formatSecondsDuration(row[col.key]);
        return row[col.key] ?? "";
      })
    );

  useImperativeHandle(ref, () => ({
    exportCsv: async () => {
      onExportingChange?.(true);
      try {
        const allRows = [];
        let cursor;
        let pageCount = 0;
        let truncated = false;

        do {
          const data = await fetchCategoryReportEventsRequest({
            category,
            startTime: startDate,
            endTime: endDate,
            memberIds: activeMemberFilterValue ? [activeMemberFilterValue._id] : undefined,
            durationMin,
            durationMax,
            groupByCount: isGrouped || undefined,
            countSortDir: isGrouped ? countSortDir : undefined,
            cursor,
            pageSize: EXPORT_PAGE_SIZE,
          });
          allRows.push(...data.rows);
          cursor = data.nextCursor;
          pageCount += 1;
          if (pageCount >= EXPORT_MAX_PAGES && cursor) {
            truncated = true;
            break;
          }
        } while (cursor);

        if (truncated) {
          toast.error("This filter matches too much data to export in full. Narrow the date range or filters and try again.");
          return;
        }

        const headers = columns.map((col) => col.label);
        downloadCsv(`reports-${category}-${new Date().toISOString().slice(0, 10)}.csv`, headers, rowsToCsvRows(columns, allRows));
      } catch (error) {
        toast.error(error.message || "Failed to export this report.");
      } finally {
        onExportingChange?.(false);
      }
    },
  }));

  const handleAddFilter = (filterKey) => {
    const filterDef = availableFilters.find((f) => f.key === filterKey);
    const initialValue =
      filterDef?.type === "duration"
        ? {
            operator: "gt",
            value1: "0",
            value2: "",
            unit: filterDef.units?.[0] || "",
            ...(filterDef.includesCount ? { countEnabled: false } : {}),
          }
        : null;
    setActiveFilterKeys((prev) => [...prev, filterKey]);
    setFilterValues((prev) => ({ ...prev, [filterKey]: initialValue }));
    setAddFilterAnchorEl(null);
  };

  const handleRemoveFilter = (filterKey) => {
    setActiveFilterKeys((prev) => prev.filter((k) => k !== filterKey));
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[filterKey];
      return next;
    });
  };

  const hasAnyFilterApplied =
    activeFilterKeys.length > 0 || (singleFilter && filterValues[singleFilter.key]);

  const handleClearFilters = () => {
    setActiveFilterKeys([]);
    setFilterValues({});
  };

  return (
    <Box
      ref={outerBoxRef}
      sx={{
        // Height caps at Overview's own real measured ceiling but SHRINKS
        // below it to hug real content when there's less than a full page
        // -- kills the dead-space gap (Rahim's ask, 2026-08-17). boxHeightPx
        // is computed in the effect above from real measurements (this
        // box's own top offset, matching Overview's technique exactly, see
        // that effect's comment), not from watching this box's own size,
        // so it can't feedback-loop. Falls back to a rough calc() below for
        // the one frame before the first measurement lands -- matches
        // Overview's own same-purpose fallback (ReportsTable.jsx).
        height: boxHeightPx ? `${boxHeightPx}px` : "calc(100vh - 320px)",
        // Must track boxHeightPx exactly like `height` above, NOT a fixed
        // fallback -- max-height always wins over height in the box model,
        // so a maxHeight stuck on the one-frame fallback value was silently
        // clamping the real (larger, correctly-measured) height back down
        // on every render. Real bug, caught live (2026-09-05): the box
        // measured a correct 718px internally but rendered at 580px
        // because of exactly this.
        maxHeight: boxHeightPx ? `${boxHeightPx}px` : "calc(100vh - 320px)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Header row: Title left, Date Range + filter chips + Add filter
          right -- same calendar control as Overview mode
          (ReportsDateRangeControl), not a second/duplicate one; it just
          lives here instead of the outer bar while a category is selected. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
          flexShrink: 0,
        }}
      >
        <Tooltip title={REPORTS_TOOLTIP_COPY[category] || ""} placement="right" arrow componentsProps={reportsInfoTooltipSx}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: "text.primary", ml: 1, width: "fit-content", cursor: "default" }}>
            {label}
          </Typography>
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <ReportsDateRangeControl
            startDate={startDate}
            endDate={endDate}
            onRangeChange={onRangeChange}
            maxRangeDays={isGrouped ? GROUPED_MAX_RANGE_DAYS : undefined}
          />

          {singleFilter ? (
            <CategoryMemberFilterChip
              label={singleFilter.label}
              value={filterValues[singleFilter.key]}
              members={members}
              onChange={(member) => setFilterValues((prev) => ({ ...prev, [singleFilter.key]: member }))}
            />
          ) : (
            <>
              {activeFilterKeys.map((key) => {
                const filterDef = availableFilters.find((f) => f.key === key);
                if (!filterDef) return null;
                if (filterDef.type === "member") {
                  return (
                    <CategoryMemberFilterChip
                      key={key}
                      label={filterDef.label}
                      value={filterValues[key]}
                      members={members}
                      onChange={(member) => setFilterValues((prev) => ({ ...prev, [key]: member }))}
                      onRemove={() => handleRemoveFilter(key)}
                    />
                  );
                }
                if (filterDef.type === "duration") {
                  return (
                    <CategoryDurationFilterChip
                      key={key}
                      label={filterDef.label}
                      value={filterValues[key]}
                      units={filterDef.units}
                      includesCount={filterDef.includesCount}
                      onChange={(val) => setFilterValues((prev) => ({ ...prev, [key]: val }))}
                      onRemove={() => handleRemoveFilter(key)}
                    />
                  );
                }
                return null;
              })}

              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setAddFilterAnchorEl(e.currentTarget)}
                startIcon={
                  <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <AddIcon size="100%" color="currentColor" />
                  </Box>
                }
                sx={reportsFilterPillSx}
              >
                Add filter
              </Button>
              <Menu
                anchorEl={addFilterAnchorEl}
                open={Boolean(addFilterAnchorEl)}
                onClose={() => setAddFilterAnchorEl(null)}
                // Was a bare default MUI Menu (Rahim's screenshot,
                // 2026-08-17) -- sharp corners, no border/shadow, plain
                // MenuItem padding, reading as an old leftover style next to
                // every other dropdown on this page (Select Category, All
                // members, the column-header filters), which all share
                // reportsDropdownPaperSx + this same item spacing.
                slotProps={{ paper: { sx: { mt: 0.75, ...reportsDropdownPaperSx } } }}
              >
                {availableFilters.length === 0 && (
                  <MenuItem disabled sx={{ fontSize: "0.9rem", py: 0.95, px: 1.625, mx: 0.5, borderRadius: "8px" }}>
                    Filters coming soon
                  </MenuItem>
                )}
                {availableFilters.length > 0 && remainingFilters.length === 0 && (
                  <MenuItem disabled sx={{ fontSize: "0.9rem", py: 0.95, px: 1.625, mx: 0.5, borderRadius: "8px" }}>
                    No more filters
                  </MenuItem>
                )}
                {remainingFilters.map((f) => (
                  <MenuItem
                    key={f.key}
                    onClick={() => handleAddFilter(f.key)}
                    sx={{ fontSize: "0.9rem", py: 0.95, px: 1.625, mb: 0.25, mx: 0.5, borderRadius: "8px" }}
                  >
                    {f.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {hasAnyFilterApplied && (
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: "none", color: "text.secondary", fontSize: "0.85rem", fontWeight: 600, minWidth: 0, px: 1 }}
            >
              Clear
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer
        ref={tableContainerRef}
        onScroll={handleTableScroll}
        sx={{
          // flex:1 + minHeight:0 -- the root Box above now owns the fixed
          // height + flex column; this just takes whatever's left after
          // the header row and pagination (both flexShrink:0) keep their
          // natural size.
          flex: 1,
          minHeight: 0,
          // Infinite scroll (replaces Previous/Next pages): the rows scroll inside this
          // fixed-height box and the next page is fetched near the bottom (onScroll below).
          overflowY: "auto",
          // Scrollbar: same thin rounded grey one as Overview's table (ReportsTable.jsx's
          // TableContainer) so both tables look identical, instead of the browser default.
          scrollbarWidth: "thin",
          scrollbarColor: (t) =>
            t.palette.mode === "dark"
              ? `${t.palette.grey[600]} ${t.palette.grey[900]}`
              : `${t.palette.grey[500]} ${t.palette.grey[100]}`,
          "&::-webkit-scrollbar": {
            width: { xs: "4px", sm: "4px", md: "4px", lg: "6px", xl: "6px" },
            height: { xs: "4px", sm: "4px", md: "4px", lg: "6px", xl: "6px" },
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100]),
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[600] : t.palette.grey[400]),
            borderRadius: "8px",
            border: (t) =>
              t.palette.mode === "dark" ? `2px solid ${t.palette.grey[900]}` : `2px solid ${t.palette.grey[100]}`,
            backgroundClip: "padding-box",
            "&:hover": {
              backgroundColor: (t) => t.palette.grey[500],
            },
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: (t) => (t.palette.mode === "dark" ? t.palette.grey[900] : t.palette.grey[100]),
          },
          // Row spacing matched to Overview's body cells -- was default MUI
          // padding (~16px), now the same tightened tier (2026-09-04,
          // "reduce the spacing a little... for the inner table as well")
          // Overview's own body cells just moved to, so both tables still
          // read consistently. Tightened again (2026-09-05, "reduce the top
          // down spac[ing] so a lot of data can be seen in one view",
          // matching the same Overview ask) -- was 11-16px vertical.
          "& .MuiTableBody-root .MuiTableCell-root": {
            paddingTop: { xs: "7px", sm: "8px", md: "9px", lg: "9px", xl: "10px" },
            paddingBottom: { xs: "7px", sm: "8px", md: "9px", lg: "9px", xl: "10px" },
            paddingLeft: { xs: "10px", sm: "12px", md: "13px", lg: "13px", xl: "14px" },
            paddingRight: { xs: "10px", sm: "12px", md: "13px", lg: "13px", xl: "14px" },
          },
          // Header band -- forgot this the first pass. Same grey-band +
          // rounded-corner treatment as Overview's header.
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)"),
            borderBottom: "none",
            paddingTop: { xs: "8px", sm: "10px", md: "11px", lg: "12px", xl: "13px" },
            paddingBottom: { xs: "8px", sm: "10px", md: "11px", lg: "12px", xl: "13px" },
            paddingLeft: { xs: "10px", sm: "12px", md: "13px", lg: "13px", xl: "14px" },
            paddingRight: { xs: "10px", sm: "12px", md: "13px", lg: "13px", xl: "14px" },
          },
          "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:first-of-type": {
            borderTopLeftRadius: "12px",
          },
          "& .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:last-of-type": {
            borderTopRightRadius: "12px",
          },
        }}
      >
        <Table stickyHeader sx={{ borderCollapse: "collapse" }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.key === "count" ? (
                    <TableSortLabel
                      active
                      direction={countSortDir}
                      onClick={() => setCountSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      <Skeleton width="70%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ color: "error.main", fontSize: "14px", textAlign: "center", py: 4 }}>
                  {/* Shows the backend's own message when there is one (e.g.
                      the "Filter by Count" safety refusal explains exactly
                      what to do -- narrow the range/members), generic
                      fallback otherwise. */}
                  {typeof error === "string" && error ? error : "Couldn't load this data. Try again in a moment."}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              // Matches Overview's empty state (ReportsTable.jsx) -- title +
              // subtitle, centered, filling the same fixed-height box
              // instead of a single terse line sitting at the top.
              <TableRow data-empty-state-row>
                <TableCell colSpan={columns.length} sx={{ border: "none", py: 8 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "text.primary" }}>
                      No data yet
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                      Try adjusting or clearing your filters.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={row.id ?? `${row.conversationId}_${row.performerName ?? ""}_${rowIndex}`} sx={{ "&:hover": { backgroundColor: "action.hover" } }}>
                  {columns.map((col) => (
                    <TableCell key={col.key} sx={{ color: "text.primary", fontSize: "14px", whiteSpace: "nowrap" }}>
                      {col.key === "timestamp" || col.key === "lastTimestamp" ? (
                        formatTimestamp(row[col.key])
                      ) : col.key === "time" ? (
                        formatSecondsDuration(row[col.key])
                      ) : col.key === "conversationId" ? (
                        row.conversationId ? (
                          // Raw contact id, clickable straight into the real
                          // chat in Inbox -- Rahim's explicit call, 2026-08-15
                          // ("I don't want the name just use the id").
                          <Box
                            component="button"
                            onClick={() => goToConversation(row.conversationId)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              border: "none",
                              background: "none",
                              p: 0,
                              cursor: "pointer",
                              color: "primary.main",
                              fontSize: "14px",
                              fontFamily: "inherit",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {row.conversationId}
                            <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <ExportSquare size="100%" color="currentColor" />
                            </Box>
                          </Box>
                        ) : (
                          "—"
                        )
                      ) : (
                        row[col.key] ?? "—"
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
            {!loading && !error && loadingMore && (
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    <Skeleton width="70%" />
                  </TableCell>
                ))}
              </TableRow>
            )}
            {!loading && !error && loadMoreError && (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: "center", py: 1.5, border: "none" }}>
                  <Typography component="span" sx={{ fontSize: "0.85rem", color: "error.main", mr: 1 }}>
                    Couldn't load more rows.
                  </Typography>
                  <Box
                    component="button"
                    onClick={loadMore}
                    sx={{ border: "none", background: "none", p: 0, cursor: "pointer", color: "primary.main", fontSize: "0.85rem", fontFamily: "inherit", "&:hover": { textDecoration: "underline" } }}
                  >
                    Retry
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
});

export default CategoryReportDetail;
